import type {ReactElement} from 'react';
import React, {useEffect, useRef} from 'react';
import {Icon} from '@Components/icon-v2';
import {executeThunk} from '@Utils/thunk.util';
import {getBrandsAndActiveBrandsForUser, getContentForBrandContentType} from '@Components/mystuff-brands/brands-thunk';
import {Text, TextSize} from '@Components/text';
import {
  areBrandsInEmptyState,
  areBrandsLoaded,
  canUserAccessBrandKits,
  fetchBrandsResourceIfNotLoaded,
  isBrandContentLoading,
  isThereNoBrandContent,
} from '@Libraries/brands-library';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import {Button, Type} from '@Components/button';
import {BrandAssetName} from '@Components/editor-brands/editor-brands.types';
import {DottedIconButton} from '@Components/dotted-icon-button';
import BrandsHeader from '@Components/editor-brands/components/brands-header';
import {setBrandsLoadingState} from '@Components/mystuff-brands/brands-reducer';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {noop} from '@Utils/general.util';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {useAppDispatch} from '@/hooks';
import styles from './editor-brands.module.scss';
import {useSortedBrandKitsFromStore} from '@Hooks/brands/useSortedBrandKitsFromStore';
import {useActiveBrandId} from '@Hooks/brands/useActiveBrandId';
import {useBrandContentMap} from '@Hooks/brands/useBrandContentMap';
import {useBrandsLoadingState} from '@Hooks/brands/useBrandsLoadingState';
import {BrandsLoadingState} from '@Components/mystuff-brands/brand-kits.types';
import {redirectUser} from '@Utils/browser.util';

const containerQuery = {
  small: {
    minWidth: 0,
    maxWidth: 460,
  },
  large: {
    minWidth: 461,
  },
};

interface EditorBrandsProps extends BrandsSectionProperties {
  className?: string;
  children: ReactElement<any>;
  isSmallSection?: boolean;
  showSmallDropdownHeader?: boolean;
  brandAsset: BrandAssetName;

  onAddBrandAsset(cb: () => void): void;
}

export interface BrandsSectionProperties {
  dontOpenBrandsDropdown?: boolean;
  removeDefaultPadding?: boolean;
  onClickBrandsSelector?(): void;
}

function EditorBrands({
  dontOpenBrandsDropdown = false,
  removeDefaultPadding = false,
  onClickBrandsSelector = noop,
  className = '',
  showSmallDropdownHeader = false,
  ...props
}: EditorBrandsProps): ReactElement<any> | null {
  const brands = useSortedBrandKitsFromStore();
  const activeBrandId: string = useActiveBrandId() ?? '';
  const brandsLoadingState: BrandsLoadingState = useBrandsLoadingState();
  const brandContent = useBrandContentMap();
  const brandContentLoadingState = brandContent[activeBrandId] ? brandContent[activeBrandId][props.brandAsset].loaded : 0;
  const contentRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(contentRef, containerQuery);
  const dispatch = useAppDispatch();

  const fetchBrands = (): void => {
    if (canUserAccessBrandKits()) {
      void executeThunk(() => {
        return dispatch(getBrandsAndActiveBrandsForUser());
      });
    } else {
      dispatch(setBrandsLoadingState(BrandsLoadingState.LOADED));
    }
  };

  useEffect((): void => {
    void fetchBrandsResourceIfNotLoaded(brandsLoadingState, fetchBrands);
  }, []);

  const getBrandContentType = (): number => {
    switch (props.brandAsset) {
      case BrandAssetName.LOGOS:
        return 1;
      case BrandAssetName.COLORS:
        return 2;
      case BrandAssetName.FONTS:
      default:
        return 3;
    }
  };

  const fetchBrandContent = (): void => {
    void executeThunk(() => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return dispatch(getContentForBrandContentType({brandId: activeBrandId, brandContentType: getBrandContentType()}));
    });
  };

  useEffect(() => {
    void fetchBrandsResourceIfNotLoaded(brandContentLoadingState, fetchBrandContent);
  }, [brandContentLoadingState]);

  const noBrandsInfo = (): ReactElement<any> => {
    return (
      <div className="flex-column-align-center flex-content-center _full-height _full-width">
        <Icon icon="icon-brands" type={IconType.FLAT} size={IconSize.SIZE_ICON_32} shape={IconShape.CIRCLE} />
        <Text className="spacing-p-t-5" val={window.i18next.t('pmwjs_no_brand_message')} size={TextSize.SMALL} />
        <Button
          customClasses="spacing-m-t-5"
          text={window.i18next.t('pmwjs_create_a_brand')}
          type={Type.SECONDARY}
          icon="icon-plus"
          onClick={(): void => {
            props.onAddBrandAsset(goToMyStuff);
          }}
        />
      </div>
    );
  };

  const noBrandsInfoForNonPremiumUser = (): ReactElement<any> => {
    return (
      <div className="flex-column-align-center flex-content-center _full-height _full-width">
        <div className={`flex-center radius-round ${styles.premiumIcon}`}>
          <Icon icon="icon-crown" type={IconType.FLAT} size={IconSize.SIZE_ICON_32} shape={IconShape.NONE} />
        </div>
        <Text className="spacing-p-t-5" val={window.i18next.t('pmwjs_upgrade_account_for_brands')} size={TextSize.SMALL} />
        <Button
          customClasses="spacing-m-t-5"
          text={window.i18next.t('pmwjs_upgrade')}
          type={Type.SECONDARY}
          onClick={(): void => {
            props.onAddBrandAsset(goToMyStuff);
          }}
        />
      </div>
    );
  };

  const getCircularLoader = (): ReactElement<any> => {
    if (props.brandAsset === BrandAssetName.LOGOS) {
      return <img className="spacing-p-t-2" src={window.PMW.util.asset_url('images/postermaker/logos-loading.png')} alt="logos" />;
    }

    return (
      <div className={`flex-center ${props.isSmallSection ? ' spacing-m-t-6' : '_full-width _full-height'}`}>
        <div>
          <CircularProgressLoader size={props.isSmallSection ? LOADER_SIZE.DEFAULT : LOADER_SIZE.LARGE} />
        </div>
      </div>
    );
  };

  const goToMyStuff = (): void => {
    redirectUser(window.PMW.util.site_url('posters/mine#/brands'), false, false);
  };

  const getUploadBtnText = (asset?: string): string => {
    switch (asset) {
      case BrandAssetName.LOGOS:
        return window.i18next.t('pmwjs_upload_a_logo');
      case BrandAssetName.COLORS:
        return window.i18next.t('pmwjs_add_a_color');
      case BrandAssetName.FONTS:
        return window.i18next.t('pmwjs_add_a_font');
      default:
        return window.i18next.t('pmwjs_add_a_brand');
    }
  };

  const getNoContentButton = (asset?: string): ReactElement<any> => {
    return (
      <div className={`${styles.dottedButtonContainer} ${params.large ? styles.maxWidth : ''}`}>
        <DottedIconButton
          btnText={getUploadBtnText(asset)}
          btnIcon="icon-upload content-body"
          direction="horizontal"
          textClasses="body-xs"
          className="spacing-m-t-3 _full-width"
          onClick={(): void => {
            props.onAddBrandAsset(goToMyStuff);
          }}
        />
      </div>
    );
  };

  const getContent = (): ReactElement<any> | null => {
    const isContentLoading = isBrandContentLoading(activeBrandId, brandContent);
    if (isContentLoading) {
      return getCircularLoader();
    }

    if (isThereNoBrandContent(activeBrandId, brandContent, props.brandAsset)) {
      return getNoContentButton(props.brandAsset);
    }

    return props.children;
  };

  if (brandsLoadingState === BrandsLoadingState.NOT_LOADED) {
    return null;
  }

  if (brandsLoadingState === BrandsLoadingState.LOADING) {
    return getCircularLoader();
  }

  if (!canUserAccessBrandKits() || (areBrandsLoaded(brandsLoadingState) && areBrandsInEmptyState(brands, brandsLoadingState))) {
    if (props.isSmallSection) {
      return (
        <div className={`${className} ${removeDefaultPadding ? '' : 'spacing-p-t-6 spacing-p-b-6'}`}>
          <BrandsHeader
            onClickDropdownSelector={(): void => {
              onClickBrandsSelector();
            }}
            showDropdownSelectorOnly={dontOpenBrandsDropdown}
            showSmallDropdownHeader={showSmallDropdownHeader}
            brandAsset={props.brandAsset}
            isSmallSection={props.isSmallSection}
          />
          {getNoContentButton()}
        </div>
      );
    }
    if (!canUserAccessBrandKits()) {
      return noBrandsInfoForNonPremiumUser();
    }
    return noBrandsInfo();
  }

  return (
    <div className={className} ref={contentRef}>
      <BrandsHeader
        showDropdownSelectorOnly={dontOpenBrandsDropdown}
        onClickDropdownSelector={(): void => {
          onClickBrandsSelector();
        }}
        showSmallDropdownHeader={showSmallDropdownHeader}
        brandAsset={props.brandAsset}
      />
      {getContent()}
    </div>
  );
}

export default EditorBrands;
